package com.tovtalk.ui.screen.home


import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Reply
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Bookmark
import androidx.compose.material.icons.filled.BookmarkBorder
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.ChatBubbleOutline
import androidx.compose.material3.Text
import androidx.compose.ui.unit.sp
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.tovtalk.utils.format.formatCount

import androidx.compose.foundation.layout.statusBarsPadding
import com.tovtalk.data.local.TokenManager
import com.tovtalk.di.getKoin
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.FeedItem
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.viewmodel.home.HomeViewModel
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.ui.components.KashrutRestrictionDialog
import com.tovtalk.ui.components.TovTalkEmptyFeedState
import com.tovtalk.ui.components.TovTalkFullScreenLoading
import com.tovtalk.ui.components.ReelFeedContent
import com.tovtalk.ui.platform.setReelContainerVisible
import com.tovtalk.ui.screen.profile.ProfileFeedScreen
import com.tovtalk.ui.screen.profile.ProfileScreen
import com.tovtalk.ui.screen.search.SearchScreen
import com.tovtalk.ui.screen.upload.UploadScreen
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreen(
    onNavigateToProfile: (userId: Int, fromPostId: Int?) -> Unit,
    onNavigateToPost: (userId: Int, postId: Int) -> Unit,
    onNavigateToPostDetail: (postRef: String, post: Post?) -> Unit,
    onNavigateToChat: (Int, String) -> Unit,
    onNavigateToRequests: () -> Unit,
    onNavigateToSettings: (() -> Unit)? = null,
    onNavigateToEditProfile: (() -> Unit)? = null,
    onNavigateToMessages: (() -> Unit)? = null,
    onLogout: (() -> Unit)? = null,
    onNavigateToLogin: (() -> Unit)? = null,
    onShowError: (String) -> Unit = {},
    onVideoBack: () -> Unit = {},
    onNavigateToTab: ((Int) -> Unit)? = null,
    onReelsLoaded: ((List<Post>) -> Unit)? = null,
    onReelIndexChanged: ((Int, List<Post>) -> Unit)? = null,
    onPreloadNextVideoUrl: ((String?) -> Unit)? = null,
    onDiscoverFirstPage: ((List<Post>) -> Unit)? = null,
    onNavigateToFollowers: ((Int) -> Unit)? = null,
    onNavigateToFollowing: ((Int) -> Unit)? = null,
    initialTab: Int = 0,
    initialPlayUserId: Int? = null,
    initialPlayPostId: Int? = null,
    startPostRef: String? = null,
    onHashtagClick: (String) -> Unit = {},
    onNavigateToStories: ((Int) -> Unit)? = null,
    viewModel: HomeViewModel = koinInject()
) {
    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode
    var selectedTab by rememberSaveable { mutableIntStateOf(initialTab) }
    LaunchedEffect(initialTab) { selectedTab = initialTab }
    LaunchedEffect(startPostRef) {
        val ref = startPostRef ?: return@LaunchedEffect
        viewModel.setPinnedPost(ref)
    }
    LaunchedEffect(isGuestMode) {
        if (isGuestMode && selectedTab >= 2) selectedTab = 0  // guest: only Home(0), Search(1)
    }
    var overlayVideo by remember { mutableStateOf<Pair<Int, Int>?>(null) }
    val authSheetController: AuthSheetController = koinInject()
    val uploadSnackbarHostState = remember { SnackbarHostState() }
    var pendingPostNavigation by remember { mutableStateOf<Pair<Int, Int>?>(null) }
    val scope = rememberCoroutineScope()
    val userRepository: UserRepository = getKoin().get()
    val cachedUser by userRepository.getCachedUser().collectAsState(initial = null)
    val currentUserIsReadOnly = cachedUser?.isReadOnly == true
    val showKashrutDialog by viewModel.showKashrutDialog.collectAsState()
    val kashrutDialogMessage by viewModel.kashrutDialogMessage.collectAsState()

    val showVideo = overlayVideo != null || (initialPlayUserId != null && initialPlayPostId != null)
    val videoUserId = overlayVideo?.first ?: initialPlayUserId ?: 0
    val videoPostId = overlayVideo?.second ?: initialPlayPostId ?: 0

    // Bottom bar is provided by the outer AdaptiveScaffold/AppScaffold in TovTalkNavHost,
    // so we don't add one here. Use PaddingValues(0.dp) since padding is handled by the outer scaffold.
    val paddingValues = PaddingValues(0.dp)
    Box(modifier = Modifier.fillMaxSize()) {
        LaunchedEffect(selectedTab) {
            setReelContainerVisible(selectedTab == 0)
        }
        Box(modifier = Modifier.fillMaxSize()) {
            if (showKashrutDialog) {
                KashrutRestrictionDialog(
                    message = kashrutDialogMessage,
                    onDismiss = { viewModel.dismissKashrutDialog() },
                    onGoToSettings = if (currentUserIsReadOnly) {
                        {
                            viewModel.dismissKashrutDialog()
                            onNavigateToSettings?.invoke()
                        }
                    } else null
                )
            }
            SnackbarHost(
                hostState = uploadSnackbarHostState,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .statusBarsPadding()
            )
            if (showVideo) {
                Box(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
                    ProfileFeedScreen(
                        userId = videoUserId,
                        startPostId = videoPostId,
                        onNavigateBack = {
                            if (overlayVideo != null) overlayVideo = null else onVideoBack()
                        },
                        onNavigateToProfile = { userId -> onNavigateToProfile(userId, null) },
                        onNavigateToMyProfile = { selectedTab = 3 },
                        onFollowUser = { },
                        showBackButton = false,
                        onHashtagClick = onHashtagClick,
                        onNavigateToLogin = onNavigateToLogin,
                    )
                }
            } else {
                val effectiveTab = if (isGuestMode && selectedTab >= 2) 0 else selectedTab
                when (effectiveTab) {
                    0 -> {
                        var currentReelPosterId by remember { mutableStateOf<Int?>(null) }
                        val feedProfileHorizontalPagerState = androidx.compose.foundation.pager.rememberPagerState(
                            initialPage = 0,
                            pageCount = { 2 }
                        )
                        val feedProfilePagerScope = rememberCoroutineScope()
                        com.tovtalk.ui.platform.PlatformBackHandler(
                            enabled = feedProfileHorizontalPagerState.currentPage > 0
                        ) {
                            feedProfilePagerScope.launch {
                                feedProfileHorizontalPagerState.animateScrollToPage(0)
                            }
                        }
                        androidx.compose.foundation.pager.HorizontalPager(
                            state = feedProfileHorizontalPagerState,
                            modifier = Modifier.fillMaxSize()
                        ) { page ->
                            when (page) {
                                0 -> HomeFeedContent(
                                    viewModel = viewModel,
                                    paddingValues = paddingValues,
                                    onNavigateToProfile = onNavigateToProfile,
                                    onNavigateToMyProfile = { selectedTab = 3 },
                                    isGuestMode = isGuestMode,
                                    onGuestActionRequired = onNavigateToLogin,
                                    onShowGuestPromptSheet = { authSheetController.open() },
                                    onShowGuestReportPrompt = { authSheetController.open(reason = "guest_prompt_report_title") },
                                    onReelsLoaded = onReelsLoaded,
                                    onReelIndexChanged = { index, posts ->
                                        // index is pager index (includes ads), so look up
                                        // the current post from feedItems, not the ad-free list.
                                        val feed = viewModel.feedItemsState.value
                                        val post = (feed.getOrNull(index) as? FeedItem.VideoItem)?.post
                                        currentReelPosterId = post?.author?.id
                                        onReelIndexChanged?.invoke(index, posts)
                                    },
                                    onPreloadNextVideoUrl = onPreloadNextVideoUrl,
                                    onDiscoverFirstPage = onDiscoverFirstPage,
                                    onOpenSearch = { selectedTab = 1 },
                                    onShowError = onShowError,
                                    onHashtagClick = onHashtagClick,
                                    startPostRef = startPostRef,
                                    onNavigateToStories = onNavigateToStories,
                                )
                                1 -> {
                                    val posterId = currentReelPosterId
                                    if (posterId != null) {
                                        ProfileScreen(
                                            userId = posterId,
                                            onNavigateToPost = { userId, postId -> overlayVideo = Pair(userId, postId) },
                                            onNavigateBack = null,
                                            onNavigateToEditProfile = onNavigateToEditProfile,
                                            onNavigateToSettings = onNavigateToSettings,
                                            onNavigateToMessages = onNavigateToMessages,
                                            onLogout = onLogout,
                                            onNavigateToFollowers = onNavigateToFollowers,
                                            onNavigateToFollowing = onNavigateToFollowing,
                                            onNavigateToStories = onNavigateToStories,
                                        )
                                    } else {
                                        Box(modifier = Modifier.fillMaxSize())
                                    }
                                }
                            }
                        }
                    }
                    else -> Box(modifier = Modifier.padding(paddingValues)) {
                        when {
                            effectiveTab == 1 -> SearchScreen(
                                onNavigateToProfile = { userId -> onNavigateToProfile(userId, null) },
                                onPostClick = { post ->
                                    com.tovtalk.data.local.PostDetailCache.set(post)
                                    onNavigateToPostDetail(post.ref, post)
                                }
                            )
                            effectiveTab == 2 -> UploadScreen(
                                onShowError = onShowError,
                                onUploadSuccess = { message, userId, postId ->
                                    pendingPostNavigation = Pair(userId, postId)
                                    selectedTab = 0  // Switch to home tab
                                    scope.launch {
                                        val result = uploadSnackbarHostState.showSnackbar(
                                            message = message,
                                            actionLabel = StringProvider.get("upload_success_watch"),
                                            duration = SnackbarDuration.Long
                                        )
                                        if (result == SnackbarResult.ActionPerformed) {
                                            overlayVideo = pendingPostNavigation
                                        }
                                        pendingPostNavigation = null
                                    }
                                }
                            )
                            effectiveTab == 3 -> ProfileScreen(
                                userId = null,
                                onNavigateToPost = { userId, postId -> overlayVideo = Pair(userId, postId) },
                                onNavigateBack = null,
                                onNavigateToEditProfile = onNavigateToEditProfile,
                                onNavigateToSettings = onNavigateToSettings,
                                onNavigateToMessages = onNavigateToMessages,
                                onLogout = onLogout,
                                onNavigateToFollowers = onNavigateToFollowers,
                                onNavigateToFollowing = onNavigateToFollowing,
                                onNavigateToStories = onNavigateToStories
                            )
                            else -> {}
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun HomeFeedContent(
    viewModel: HomeViewModel,
    paddingValues: PaddingValues,
    onNavigateToProfile: (userId: Int, fromPostId: Int?) -> Unit,
    onNavigateToMyProfile: () -> Unit = {},
    isGuestMode: Boolean = false,
    onGuestActionRequired: (() -> Unit)? = null,
    onShowGuestPromptSheet: (() -> Unit)? = null,
    onShowGuestReportPrompt: (() -> Unit)? = null,
    onReelsLoaded: ((List<Post>) -> Unit)? = null,
    onReelIndexChanged: ((Int, List<Post>) -> Unit)? = null,
    onPreloadNextVideoUrl: ((String?) -> Unit)? = null,
    onDiscoverFirstPage: ((List<Post>) -> Unit)? = null,
    onOpenSearch: () -> Unit = {},
    onShowError: (String) -> Unit = {},
    onHashtagClick: (String) -> Unit = {},
    startPostRef: String? = null,
    onNavigateToStories: ((Int) -> Unit)? = null,
) {
    val postsState by viewModel.postsState.collectAsState()
    val feedItems by viewModel.feedItemsState.collectAsState()
    val currentUserId by viewModel.currentUserId.collectAsState()
    val audioAutoplay by viewModel.audioAutoplay.collectAsState()
    val saveError by viewModel.saveError.collectAsState()
    val guestReactionsIconVisible by viewModel.guestReactionsIconVisible.collectAsState()
    val showReactionIcon = !isGuestMode || guestReactionsIconVisible
    val adsRepository: AdsRepository = koinInject()
    val scope = rememberCoroutineScope()
    // Wide-screen FeedSideActions sits outside ReelFeedContent so its internal sheet state
    // isn't reachable; these states drive duplicate Share/Comments sheets hosted here.
    var sideCommentPostId by rememberSaveable { mutableStateOf<Int?>(null) }
    var sideSharePostRef by rememberSaveable { mutableStateOf<String?>(null) }
    // Mirror ReelFeedContent's comment gating so the wide-screen side panel respects kashrut level.
    val appRepository: com.tovtalk.domain.repository.AppRepository = remember { getKoin().get() }
    val userRepository: com.tovtalk.domain.repository.UserRepository = remember { getKoin().get() }
    val cachedUser by userRepository.getCachedUser().collectAsState(initial = null)
    var kosherLevels by remember { mutableStateOf<List<com.tovtalk.domain.entity.KosherLevel>>(emptyList()) }
    LaunchedEffect(Unit) {
        when (val result = appRepository.getKosherLevels()) {
            is com.tovtalk.domain.entity.DomainResult.Success -> kosherLevels = result.data
            else -> {} // keep empty — commentsEnabled defaults to true
        }
    }
    val commentsEnabled = remember(kosherLevels, cachedUser) {
        kosherLevels.find { it.id == cachedUser?.kashrutLevel }?.allowTextComments ?: true
    }

    LaunchedEffect(saveError) {
        saveError?.let { msg ->
            onShowError(msg)
            viewModel.clearSaveError()
        }
    }

    // Load discover only when feed tab is visible (not in ViewModel.init — avoids discover on Messages/Notifications tab)
    // Skip if startPostRef is set — setPinnedPost (called from HomeScreen) handles the load in that case
    LaunchedEffect(Unit) {
        if (postsState.items.isEmpty() && !postsState.isLoading && startPostRef == null) {
            viewModel.loadPosts(onFirstPageLoaded = onDiscoverFirstPage)
        }
    }

    // Notify platform (e.g. Android VideoPrefetchManager) when reels list is available
    LaunchedEffect(postsState.items) {
        if (postsState.items.isNotEmpty()) onReelsLoaded?.invoke(postsState.items)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.backgroundPrimary)
    ) {
        when {
            postsState.isLoading && postsState.items.isEmpty() -> {
                TovTalkFullScreenLoading(message = StringProvider.get("common_loading"))
            }
            postsState.items.isEmpty() && !postsState.isLoading -> {
                TovTalkEmptyFeedState(modifier = Modifier.fillMaxSize())
                HomeTopQuickActions(onOpenSearch = onOpenSearch, overVideo = false)
            }
            else -> {
                val savedPage by viewModel.savedReelPage.collectAsState()
                val refreshTick by viewModel.refreshTick.collectAsState()

                // localPage updated directly via snapshotFlow callback — every page
                // change is captured, no cancellation, no frame skips.
                var localPage by remember { mutableIntStateOf(savedPage) }
                // PERF(8): on refresh, ReelFeedContent is re-keyed on refreshTick which resets
                // the pager to page 0, but localPage kept its old value until the next
                // onPageChanged fired — so the wide-screen side panel briefly read currentPost
                // from a stale index. Reset localPage to 0 when refreshTick changes so the side
                // panel stays in sync with the freshly reset pager.
                LaunchedEffect(refreshTick) { localPage = 0 }
                val feedItem = feedItems.getOrNull(localPage)
                val currentPost = (feedItem as? FeedItem.VideoItem)?.post
                com.tovtalk.utils.logger.Logger.d(
                    "SIDE_PANEL: localPage=$localPage savedPage=$savedPage " +
                    "type=${feedItem?.let { it::class.simpleName } ?: "null"} " +
                    "postId=${currentPost?.id} author=${currentPost?.author?.username} " +
                    "feedSize=${feedItems.size}",
                    "HomeScreen"
                )

                val reelContent: @Composable () -> Unit = {
                    // Re-keying on refreshTick resets pagerState so the user lands on page 0
                    // (fresh first video) instead of whatever they were watching before.
                    androidx.compose.runtime.key(refreshTick) {
                    ReelFeedContent(
                        feedItems = feedItems,
                        initialPage = savedPage,
                        adsRepository = adsRepository,
                        currentUserId = currentUserId,
                        isGuestMode = isGuestMode,
                        showReactionIcon = showReactionIcon,
                        audioAutoplay = audioAutoplay,
                        onToggleMute = { viewModel.toggleAudioAutoplay() },
                        bottomPadding = paddingValues.calculateBottomPadding(),
                        onLikePost = { id, isLiked -> viewModel.likePost(id, isLiked) },
                        onSavePost = { id, isSaved -> viewModel.savePost(id, isSaved) },
                        onFollowUser = { viewModel.followUser(it) },
                        onCommentAdded = { viewModel.incrementCommentCount(it) },
                        onUserClick = { userId, curUserId, postId ->
                            if (userId == curUserId) onNavigateToMyProfile()
                            else onNavigateToProfile(userId, postId)
                        },
                        onNavigateToProfile = { userId -> onNavigateToProfile(userId, null) },
                        onHashtagClick = onHashtagClick,
                        onPageChanged = { page ->
                            val pageItem = feedItems.getOrNull(page)
                            com.tovtalk.utils.logger.Logger.d(
                                "PAGE_CHANGED: page=$page type=${pageItem?.let { it::class.simpleName } ?: "null"} " +
                                "postId=${(pageItem as? FeedItem.VideoItem)?.post?.id}",
                                "HomeScreen"
                            )
                            localPage = page
                            viewModel.setSavedReelPage(page)
                            viewModel.onPostVisible(page)
                            onReelIndexChanged?.invoke(page, postsState.items)
                        },
                        onViewCountReached = { viewModel.increaseViewCount(it) },
                        onLoadMore = {},  // HomeViewModel loads automatically
                        onGuestActionRequired = onGuestActionRequired,
                        onShowGuestPromptSheet = onShowGuestPromptSheet,
                        onShowGuestReportPrompt = onShowGuestReportPrompt,
                        topContent = { HomeTopQuickActions(onOpenSearch = onOpenSearch) },
                        onNavigateToStories = onNavigateToStories,
                        // PERF(1): pass the already-computed commentsEnabled so ReelFeedContent
                        // does NOT fetch getKosherLevels() again on the feed path.
                        commentsEnabledOverride = commentsEnabled,
                    )
                    }
                }

                when {
                    // Mobile: Compact + MediumCompact — full overlay on video
                    com.tovtalk.ui.adaptive.isMobile() -> {
                        reelContent()
                    }
                    // Wide-screen: Medium+ — clean video with side panel
                    else -> {
                        val buttonSize = com.tovtalk.ui.adaptive.adaptive(
                            compact = 48.dp,
                            mediumCompact = 48.dp,
                            medium = 48.dp,
                            expanded = 52.dp,
                            large = 56.dp
                        )
                        val iconSize = com.tovtalk.ui.adaptive.adaptive(
                            compact = 24.dp,
                            mediumCompact = 24.dp,
                            medium = 24.dp,
                            expanded = 26.dp,
                            large = 28.dp
                        )
                        Row(
                            modifier = Modifier.fillMaxSize().background(Color.Black),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxHeight()
                                    .aspectRatio(9f / 16f)
                            ) {
                                reelContent()
                            }
                            Spacer(modifier = Modifier.width(20.dp))
                            currentPost?.let { post ->
                                val showFollow = currentUserId != null &&
                                        post.author.id != currentUserId &&
                                        !post.viewerState.isFollowing
                                FeedSideActions(
                                    post = post,
                                    onLikeClick = { viewModel.likePost(post.id, post.viewerState.isLiked) },
                                    onCommentClick = { sideCommentPostId = post.id },
                                    onShareClick = { sideSharePostRef = post.ref },
                                    onSaveClick = { viewModel.savePost(post.id, post.viewerState.isSaved) },
                                    onUserClick = { onNavigateToProfile(post.author.id, post.id) },
                                    onFollowClick = { viewModel.followUser(post.author.id) },
                                    showFollowButton = showFollow,
                                    showCommentButton = commentsEnabled && showReactionIcon,
                                    buttonSize = buttonSize,
                                    iconSize = iconSize,
                                )
                            }
                        }
                    }
                }

                // Wide-screen side-panel sheets: Compose FeedSideActions sits OUTSIDE ReelFeedContent
                // so it can't use ReelFeedContent's internal state. Host the sheets here instead.
                sideSharePostRef?.let { ref ->
                    com.tovtalk.ui.components.ShareBottomSheet(
                        deepLinkUrl = com.tovtalk.utils.deeplink.DeepLinkBuilder.buildPostUrl(ref),
                        onDismiss = { sideSharePostRef = null }
                    )
                }
                sideCommentPostId?.let { postId ->
                    val postOwnerId = feedItems
                        .filterIsInstance<FeedItem.VideoItem>()
                        .firstOrNull { it.post.id == postId }
                        ?.post?.author?.id ?: 0
                    com.tovtalk.ui.components.CommentsBottomSheet(
                        postId = postId,
                        postOwnerId = postOwnerId,
                        onDismiss = { sideCommentPostId = null },
                        onNavigateToProfile = { uid -> onNavigateToProfile(uid, null) },
                        onCommentAdded = { viewModel.incrementCommentCount(postId) },
                        isGuestMode = isGuestMode,
                        onGuestAction = { onGuestActionRequired?.invoke() },
                    )
                }

                // Notify platform of next video URL for prefetch
                LaunchedEffect(postsState.items) {
                    if (postsState.items.isNotEmpty()) {
                        val nextPost = postsState.items.getOrNull(1)
                        onPreloadNextVideoUrl?.invoke(nextPost?.videoUrl)
                    }
                }
            }
        }
    }
}

/**
 * Vertical action buttons shown BESIDE the video for non-compact breakpoints.
 * Includes profile avatar with follow badge, like, comment, save, and share.
 * For Compact, actions remain overlaid on the video inside ReelItem itself.
 */
@Composable
private fun FeedSideActions(
    post: com.tovtalk.domain.entity.Post,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit,
    onShareClick: () -> Unit,
    onSaveClick: () -> Unit,
    onUserClick: () -> Unit,
    onFollowClick: () -> Unit,
    showFollowButton: Boolean,
    showCommentButton: Boolean,
    buttonSize: androidx.compose.ui.unit.Dp,
    iconSize: androidx.compose.ui.unit.Dp,
) {
    val iconTint = Color.White

    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Profile avatar with follow badge
        Box(contentAlignment = Alignment.BottomCenter) {
            com.tovtalk.ui.components.TovTalkAvatar(
                imageUrl = post.author.profilePhoto,
                size = buttonSize + 4.dp,
                name = post.author.fullname ?: post.author.username,
                onClick = onUserClick
            )
            if (showFollowButton) {
                Box(
                    modifier = Modifier
                        .offset(y = 10.dp)
                        .size(20.dp)
                        .background(
                            color = Color(0xFFE91E63),
                            shape = CircleShape
                        )
                        .border(
                            width = 1.5.dp,
                            color = Color.Black,
                            shape = CircleShape
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = "follow",
                        tint = Color.White,
                        modifier = Modifier.size(14.dp)
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(4.dp))

        // Like
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            val likeIcon = if (post.viewerState.isLiked) Icons.Filled.Favorite else Icons.Filled.FavoriteBorder
            IconButton(onClick = onLikeClick, modifier = Modifier.size(buttonSize)) {
                Icon(likeIcon, contentDescription = "like", tint = if (post.viewerState.isLiked) Color(0xFFE91E63) else iconTint, modifier = Modifier.size(iconSize))
            }
            if (post.stats.likes > 0) {
                Text(text = formatCount(post.stats.likes), color = Color.White, fontSize = 12.sp)
            }
        }

        // Comment — hidden when kashrut level disallows text comments
        if (showCommentButton) Column(horizontalAlignment = Alignment.CenterHorizontally) {
            IconButton(onClick = onCommentClick, modifier = Modifier.size(buttonSize)) {
                Icon(Icons.Outlined.ChatBubbleOutline, contentDescription = "comment", tint = iconTint, modifier = Modifier.size(iconSize))
            }
            if (post.stats.comments > 0) {
                Text(text = formatCount(post.stats.comments), color = Color.White, fontSize = 12.sp)
            }
        }

        // Save
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            val saveIcon = if (post.viewerState.isSaved) Icons.Filled.Bookmark else Icons.Filled.BookmarkBorder
            IconButton(onClick = onSaveClick, modifier = Modifier.size(buttonSize)) {
                Icon(saveIcon, contentDescription = "save", tint = if (post.viewerState.isSaved) TovTalkTheme.colors.brand else iconTint, modifier = Modifier.size(iconSize))
            }
            if (post.stats.saves > 0) {
                Text(text = formatCount(post.stats.saves), color = Color.White, fontSize = 12.sp)
            }
        }

        // Share
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            IconButton(onClick = onShareClick, modifier = Modifier.size(buttonSize)) {
                Icon(Icons.AutoMirrored.Filled.Reply, contentDescription = "share", tint = iconTint, modifier = Modifier.size(iconSize))
            }
        }
    }
}

@Composable
private fun HomeTopQuickActions(
    onOpenSearch: () -> Unit,
    overVideo: Boolean = true,
) {
    val iconTint = if (overVideo) TovTalkTheme.colors.onBrand else TovTalkTheme.colors.textPrimary
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(horizontal = TovTalkTheme.spacing.sm, vertical = TovTalkTheme.spacing.xs),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        IconButton(
            onClick = onOpenSearch,
            modifier = Modifier.size(TovTalkTheme.sizes.videoActionButton)
        ) {
            Icon(
                imageVector = Icons.Filled.Search,
                contentDescription = "search",
                tint = iconTint
            )
        }
    }
}

